import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Clear, FileDownload } from '@mui/icons-material';
import { format } from 'date-fns';
import { fetchLeaderboard, LeaderboardEntry } from '../../services/visitTrackingService';
import { exportVisitLeaderboardToExcel } from '../../utils/exportVisitLeaderboardExcel';

function formatDuration(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds <= 0) return '0m';
  const minutes = Math.round(totalSeconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return remMinutes > 0 ? `${hours}h ${remMinutes}m` : `${hours}h`;
}

function parseLocalIsoDate(value: string): Date {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? new Date(value) : date;
}

const noWrapSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

/**
 * Small, self-contained leaderboard card — visit count + avg time spent,
 * ranked by visit count, with an optional date-range filter and an Excel
 * export. Independent loading/error state: if the very FIRST load fails,
 * it renders nothing (no error banner, no retry) — it can never affect the
 * panels above it on the page. A failure on a LATER range change instead
 * keeps the last good table visible with a small inline notice, so a bad
 * range doesn't blank out a picker the user needs to fix it.
 *
 * Date range uses a pending-value + explicit Apply pattern (same convention
 * as AutomationFilterBar.tsx elsewhere in this repo) rather than fetching on
 * every keystroke — typing a date fires several onChange events before it's
 * a complete, valid date, so auto-fetching on change either fired requests
 * against half-typed dates or made it unclear whether anything had actually
 * been applied. Apply is the one deliberate, visible trigger point, and a
 * spinner next to the range label makes the fetch visible even when the
 * resulting numbers don't look very different. Added 2026-08-25; date range
 * + export + real active-time added 2026-09-11; Apply-button rework same day
 * after local testing showed the auto-fetch version wasn't visibly updating.
 */
export default function VisitLeaderboard() {
  const [leaders, setLeaders] = useState<LeaderboardEntry[] | null>(null);
  const [ranged, setRanged] = useState(false);
  const [initialFailed, setInitialFailed] = useState(false);
  const [rangeFetchError, setRangeFetchError] = useState<string | null>(null);
  const [isFetchingRange, setIsFetchingRange] = useState(false);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // What's typed into the fields but not yet applied.
  const [pendingFrom, setPendingFrom] = useState('');
  const [pendingTo, setPendingTo] = useState('');
  // What was last actually applied — this, not the pending values, drives the fetch.
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');

  const pendingIncomplete = Boolean(pendingFrom) !== Boolean(pendingTo); // exactly one of the two set
  const pendingInvalid = Boolean(pendingFrom) && Boolean(pendingTo) && pendingFrom > pendingTo;
  const canApply = !pendingIncomplete && !pendingInvalid;

  useEffect(() => {
    let cancelled = false;
    setIsFetchingRange(true);

    fetchLeaderboard(appliedFrom || undefined, appliedTo || undefined)
      .then((result) => {
        if (cancelled) return;
        setLeaders(result.leaders);
        setRanged(result.ranged);
        setRangeFetchError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (leaders === null) {
          setInitialFailed(true);
        } else {
          setRangeFetchError(err instanceof Error ? err.message : 'Failed to load leaderboard for this range.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsFetchingRange(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFrom, appliedTo]);

  const rangeLabel = useMemo(() => {
    if (!ranged || !appliedFrom || !appliedTo) return 'All time';
    return `${format(parseLocalIsoDate(appliedFrom), 'MMM d, yyyy')} – ${format(parseLocalIsoDate(appliedTo), 'MMM d, yyyy')}`;
  }, [ranged, appliedFrom, appliedTo]);

  const handleApply = () => {
    if (!canApply) return;
    setAppliedFrom(pendingFrom);
    setAppliedTo(pendingTo);
  };

  const handleClearRange = () => {
    setPendingFrom('');
    setPendingTo('');
    setAppliedFrom('');
    setAppliedTo('');
  };

  const handleExport = async () => {
    if (!leaders || leaders.length === 0 || isExporting) return;
    setIsExporting(true);
    setExportNotice(null);
    setExportError(null);
    try {
      const { fileName, rowCount } = await exportVisitLeaderboardToExcel({
        generatedAt: new Date(),
        ranged,
        dateFrom: ranged ? appliedFrom : null,
        dateTo: ranged ? appliedTo : null,
        leaders,
      });
      setExportNotice(`Downloaded ${fileName} (${rowCount} row${rowCount === 1 ? '' : 's'}).`);
    } catch {
      setExportError('Excel export failed. Please retry.');
    } finally {
      setIsExporting(false);
    }
  };

  if (initialFailed) return null;

  if (leaders === null) {
    return (
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="rectangular" height={80} sx={{ mt: 1, borderRadius: 1 }} />
        </CardContent>
      </Card>
    );
  }

  if (leaders.length === 0) return null;

  // table-layout: fixed with px widths on the short columns (#, Visits, Avg)
  // and no width on User, which absorbs the remainder. Percentages were the
  // bug: they scale with the container, so in a narrow drawer MUI's 32px of
  // cell padding ate the whole cell and even "2" or "71h 20m" ellipsised.
  // Only User truncates now, and the table still can't exceed its container.
  const noWrapSx = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  };

  return (
    <Card variant="outlined" sx={{ width: '100%', maxWidth: '100%' }}>
      <CardContent sx={{ px: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom sx={noWrapSx}>
          Automation Insights Leaderboard
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={pendingFrom}
            onChange={(e) => setPendingFrom(e.target.value)}
            sx={{ width: 150 }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={pendingTo}
            onChange={(e) => setPendingTo(e.target.value)}
            sx={{ width: 150 }}
          />
          <Button size="small" variant="contained" onClick={handleApply} disabled={!canApply || isFetchingRange}>
            Apply
          </Button>
          {pendingFrom || pendingTo || appliedFrom || appliedTo ? (
            <Tooltip title="Clear range (show all time)">
              <IconButton size="small" onClick={handleClearRange} aria-label="Clear date range">
                <Clear fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Export this table to Excel">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownload fontSize="small" />}
                onClick={handleExport}
                disabled={isExporting || leaders.length === 0 || isFetchingRange || Boolean(rangeFetchError)}
              >
                Export
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {pendingIncomplete ? (
          <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
            Pick both a From and a To date, or clear both for all time.
          </Alert>
        ) : null}
        {pendingInvalid ? (
          <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
            The From date must be on or before the To date.
          </Alert>
        ) : null}
        {rangeFetchError ? (
          <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
            {rangeFetchError} Showing the last loaded results below.
          </Alert>
        ) : null}
        {exportError && <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setExportError(null)}>{exportError}</Alert>}
        {exportNotice ? (
          <Alert severity="success" sx={{ mb: 1, py: 0 }} onClose={() => setExportNotice(null)}>
            {exportNotice}
          </Alert>
        ) : null}

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {rangeLabel}
          </Typography>
          {isFetchingRange ? <CircularProgress size={12} /> : null}
        </Stack>

        {leaders.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No visits recorded {ranged ? 'for this range.' : 'yet.'}
          </Typography>
        ) : (
          <Table
            size="small"
            sx={{ width: '100%', tableLayout: 'fixed', '& .MuiTableCell-root': { px: 1 } }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 36 }}>#</TableCell>
                <TableCell>User</TableCell>
                <TableCell align="right" sx={{ width: 72, whiteSpace: 'nowrap' }}>
                  Visits
                </TableCell>
                <TableCell align="right" sx={{ width: 96, whiteSpace: 'nowrap' }}>
                  Avg
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leaders.map((entry, index) => (
                <TableRow key={entry.user_oid}>
                  <TableCell sx={noWrapSx}>{index + 1}</TableCell>
                  <TableCell sx={{ maxWidth: 0 }}>
                    <Typography variant="body2" sx={noWrapSx}>
                      {entry.user_name}
                    </Typography>
                    {entry.user_email ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ...noWrapSx }}>
                        {entry.user_email}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right" sx={noWrapSx}>
                    {entry.visit_count}
                  </TableCell>
                  <TableCell align="right" sx={noWrapSx}>
                    {formatDuration(entry.avg_duration_seconds)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
